<script lang="ts">
	import { getProductPath } from "../../../../core/schema/paths/getProductPath.js";
	import Link from "../Link.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import type { ProductForCustomer } from "../../../../core/schema/Product.js";
	import Button from "../Button.svelte";
	import { isProductForCustomerConfigurable } from "../../../../core/schema/utils/isProductForCustomerConfigurable.js";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import check from "../../../../core/assets/icons/check.svg?raw";
	import ProductSizeInputs from "./ProductSizeInputs.svelte";
	import { ProductSize } from "../../../../core/schema/ProductSize.js";
	import { doesProductForCustomerHaveMultipleSizes } from "../../../../core/schema/utils/doesProductForCustomerHaveMultipleSizes.js";
	import ProductCardImage from "./ProductCardImage.svelte";
	import { formatPrice } from "../../../../core/utils/formatPrice.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import ProductPriorityLabel from "./ProductPriorityLabel.svelte";
	import { reportAddedProductToCart } from "../../UniversalAnalytics.js";
	import { emptyOptions } from "../../../../core/schema/utils/emptyOptions.js";
	import NumberInput from "../Form/NumberInput.svelte";
	import Form from "../Form/Form.svelte";
	import { animateCartCount } from "../../utils/animateCartCount.js";
	import type { ProductAmountPerCategory } from "../../../../core/schema/ProductAmountPerCategory.js";
	import { getFreeSlotsForProduct } from "../../../../core/schema/utils/getFreeSlotsForProduct.js";
	import { getCartStore } from "../../contexts/cartStoreContextKey.js";
	import { getContext } from "svelte";
	import type { CakeFillingOption } from "../../../../core/schema/CakeFillingOption.js";
	import { cakeFillingsContextKey } from "../../contexts/cakeFillingsContextKey.js";
	import { requiredCategoriesContextKey } from "../../contexts/requiredCategoriesContextKey.js";
	import type { RequiredCategories } from "../../../../core/schema/namesOfRequiredCategories.js";
	import { getDefaultCakeFilling } from "../../../../core/schema/utils/getCakeFillingProducts.js";
	import { getFillingDisplayName } from "../../../../core/schema/utils/getFillingDisplayName.js";
	import { isThemedCake } from "../../../../core/schema/utils/isThemedCake.js";
	import { shouldShowProductForCustomerSize } from "../../../../core/schema/utils/shouldShowProductForCustomerSize.js";
	import { isProductCurrentlyPurchasable } from "../../../../core/schema/utils/isProductPurchasable.js";
	import { ProductOption, productOptionKeyMap } from "../../../../core/schema/ProductOption.js";
	import { minimalOptions } from "../../../../core/schema/productOptionPrices.js";
	import { getPriceOfOrderProduct } from "../../../../core/schema/utils/getPriceOfOrderProduct.js";
	import { transformProductToOrderProduct } from "../../../../core/schema/utils/transformProductToOrderProduct.js";
	import { isSimpleToggleProductOption } from "../Configurator/isSimpleToggleProductOption.js";
	import { CustomerType } from "../../../../core/schema/CustomerType.js";
	import { isProductOptionUsed } from "../../../../core/schema/utils/isProductOptionUsed.js";
	import { requiredProductOptions } from "../../../../core/schema/requiredProductOptions.js";
	import { getDefaultSizeForProductForCustomerWithExpress } from "../../../../core/schema/utils/getDefaultSizeForProductForCustomerWithExpress.js";
	import ExpressProductWarningModal from "./ExpressProductWarningModal.svelte";
	import { productSizeKeyMap } from "../../../../core/schema/productSizeKeyMap.js";
	import type { CategorySeoPage } from "../../../../core/schema/CategorySeoPage.js";
	import { isEnum } from "../../../../core/utils/enum/isEnum.js";
	import { onMount } from "svelte";
	import { ProductSizeLabelType } from "../../../../core/schema/ProductSizeLabelType.js";
	import { formatNumber } from "../../../../core/utils/formatNumber.js";
	import { inflectProductSizeLabelType } from "../../../../core/schema/names/inflectProductSizeLabelType.js";
	import { soldOutLabelNameMap } from "../../../../core/schema/names/soldOutLabelNameMap.js";
	import { SoldOutLabel } from "../../../../core/schema/SoldOutLabel.js";

	export let asCrossSale = false;
	export let isWarehouseProduct = false;
	export let product: ProductForCustomer;
	export let index = 0;
	export let maxProductsPerCategory: ProductAmountPerCategory;
	export let enabledOption: ProductOption | undefined = undefined;
	export let activeFilter: CategorySeoPage["type"] | undefined = undefined;

	let form: HTMLFormElement | null;

	const cartStore = getCartStore();
	const { TENANT_LOCALE, WEB_URL } = getGlobalSettings();

	$: priorityLabel = isWarehouseProduct ? ("express" as const) : product.priorityLabel;

	let showExpressWarningModal = false;
	let selectedSize = getDefaultSizeForProductForCustomerWithExpress(
		product,
		isWarehouseProduct,
		isEnum(ProductOption)(activeFilter) ? activeFilter : undefined,
	);

	let showSizeOverlay = false;
	let showOverlayContent = false;
	let overlayTimeout: ReturnType<typeof setTimeout> | null = null;
	let fadeTimeout: ReturnType<typeof setTimeout> | null = null;
	let isMobile = false;

	$: isInCart = $cartStore.value.order.products.some(
		({ product: { id }, size }) =>
			id === product.id && (doesProductForCustomerHaveMultipleSizes(product) ? size === selectedSize : true),
	);

	let options = structuredClone(emptyOptions);
	const cakeFillings = getContext<CakeFillingOption[] | undefined>(cakeFillingsContextKey) ?? [];
	const requiredCategories = getContext<RequiredCategories>(requiredCategoriesContextKey);
	$: displayName = product.categoryIds.includes(requiredCategories.fillings.id)
		? getFillingDisplayName(product.name[TENANT_LOCALE])
		: product.name[TENANT_LOCALE];
	$: isThemedCakeProduct = cakeFillings.length > 0 && isThemedCake(product, requiredCategories.cakes);
	$: if (isThemedCakeProduct && options.cakeFilling === undefined) {
		options = { ...options, cakeFilling: getDefaultCakeFilling(cakeFillings) };
	}
	$: customizable = isProductForCustomerConfigurable(product, false) || isThemedCakeProduct;
	$: {
		if (customizable && enabledOption !== undefined) {
			if (isSimpleToggleProductOption(enabledOption)) {
				options = {
					...options,
					[productOptionKeyMap[enabledOption]]: true,
				};
			}
		}
	}

	$: priceWithEnabledOption = getPriceOfOrderProduct(
		transformProductToOrderProduct(
			crypto.randomUUID(),
			product,
			1,
			selectedSize,
			enabledOption !== undefined && !isSimpleToggleProductOption(enabledOption)
				? { ...options, [productOptionKeyMap[enabledOption]]: minimalOptions[productOptionKeyMap[enabledOption]] }
				: options,
		),
	);
	$: maximumOrderQuantity = getFreeSlotsForProduct(product, maxProductsPerCategory, $cartStore.value.order.products);
	$: canAddToCart =
		maximumOrderQuantity >= product.minimumOrderQuantity &&
		isProductCurrentlyPurchasable(product, CustomerType.B2c, isWarehouseProduct) &&
		(!product.soldOut || $cartStore.value.properties.canAddSoldOutExpressProduct);

	$: link = `/${getProductPath(TENANT_LOCALE, product)}`;

	function withSizeQueryParam(link: string, size: ProductSize): string {
		const url = new URL(`${WEB_URL}${link}`);
		url.searchParams.set("size", size);
		return `${url.pathname}${url.search}`;
	}

	function withConfigQueryParam(link: string): string {
		const url = new URL(`${WEB_URL}${link}`);
		url.searchParams.set("config", "");
		return `${url.pathname}${url.search}`;
	}

	function withExpressQueryParam(link: string): string {
		const url = new URL(`${WEB_URL}${link}`);
		url.searchParams.set("express", "");
		return `${url.pathname}${url.search}`;
	}

	function withEnabledOptionQueryParam(link: string, option: ProductOption | undefined): string {
		const url = new URL(`${WEB_URL}${link}`);
		if (option !== undefined) {
			url.searchParams.set("option", option);
		}
		return `${url.pathname}${url.search}`;
	}

	function getLinkToProduct(
		link: string,
		product: ProductForCustomer,
		selectedSize: ProductSize,
		isWarehouseProduct: boolean,
		enabledOption: ProductOption | undefined,
	): string {
		let linkToProduct = doesProductForCustomerHaveMultipleSizes(product)
			? withSizeQueryParam(link, selectedSize)
			: link;
		linkToProduct = withEnabledOptionQueryParam(linkToProduct, enabledOption);
		if (isWarehouseProduct) {
			linkToProduct = withExpressQueryParam(linkToProduct);
		}

		return linkToProduct;
	}

	$: linkToProduct = getLinkToProduct(link, product, selectedSize, isWarehouseProduct, enabledOption);

	async function putItemToCart(formData: FormData): Promise<void> {
		for (const productSize of Object.values(ProductSize)) {
			for (const enabledOption of product.enabledOptions[productSizeKeyMap[productSize]]) {
				if (requiredProductOptions.includes(enabledOption)) {
					if (!isProductOptionUsed(enabledOption, options[productOptionKeyMap[enabledOption]])) {
						let targetUrl = withConfigQueryParam(withEnabledOptionQueryParam(link, enabledOption));
						if (isWarehouseProduct) {
							targetUrl = withExpressQueryParam(targetUrl);
						}
						window.location.href = targetUrl;
						return;
					}
				}
			}
		}

		const quantity = Number(formData.get("quantity"));

		const response = await cartStore.addProduct(
			product,
			quantity,
			product.minimumOrderQuantity,
			selectedSize,
			isWarehouseProduct,
			asCrossSale,
			customizable ? options : undefined,
		);

		reportAddedProductToCart(response.item, TENANT_LOCALE);
		animateCartCount();
	}

	async function submit(forceSubmit: boolean): Promise<void> {
		if (!form) {
			throw new Error("Form not initialized");
		}

		if ($cartStore.value.order.isExpress && !isWarehouseProduct && !forceSubmit) {
			showExpressWarningModal = true;
		} else {
			await putItemToCart(new FormData(form));
			showExpressWarningModal = false;
		}
	}

	function handleSizeChange(): void {
		showSizeOverlay = true;
		showOverlayContent = true;

		if (overlayTimeout) {
			clearTimeout(overlayTimeout);
		}
		if (fadeTimeout) {
			clearTimeout(fadeTimeout);
		}

		if (isMobile) {
			overlayTimeout = setTimeout(() => {
				showSizeOverlay = false;
				fadeTimeout = setTimeout(() => {
					showOverlayContent = false;
				}, 300);
			}, 3000);
		}
	}

	function handleCardMouseLeave(): void {
		if (!isMobile) {
			showSizeOverlay = false;
			fadeTimeout = setTimeout(() => {
				showOverlayContent = false;
			}, 300);

			if (overlayTimeout) {
				clearTimeout(overlayTimeout);
			}
		}
	}

	onMount(() => {
		const checkMobile = (): void => {
			isMobile = window.matchMedia("(max-width: 768px)").matches;
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return (): void => {
			window.removeEventListener("resize", checkMobile);
			if (overlayTimeout) {
				clearTimeout(overlayTimeout);
			}
			if (fadeTimeout) {
				clearTimeout(fadeTimeout);
			}
		};
	});
</script>

<ExpressProductWarningModal
	{product}
	bind:showExpressWarningModal
	addItem={async () => {
		await submit(true);
	}}
/>

<div
	class="flex h-fit w-full flex-col rounded-[1.375rem] border border-transparent transition-all duration-300 min-[430px]:w-[initial] min-[430px]:basis-[calc(50%-0.5rem)] sm:basis-[calc(50%-0.25rem)] sm:p-4 sm:hover:border-gray-300 md:basis-[calc(50%-0.75rem)] lg:basis-[calc((100%-(2*.75rem))/3)] xl:basis-[calc((100%-(2*1.5rem))/3)] xl:p-6"
	role="region"
	on:mouseleave={handleCardMouseLeave}
>
	<a
		href={linkToProduct}
		class="group relative block aspect-square h-auto w-full overflow-hidden rounded-lg min-[430px]:min-h-[11.9375rem] min-[430px]:min-w-[11.9375rem]"
	>
		{#if priorityLabel !== undefined}
			<ProductPriorityLabel class="absolute left-2 top-2 z-10 text-2xs font-semibold" label={priorityLabel} />
		{/if}
		<ProductCardImage
			{selectedSize}
			loading={index < 5 ? "eager" : "lazy"}
			{product}
			alt="Fotografie – {displayName}"
			size={400}
			logo={{
				class:
					"min-w-[6rem] max-w-[28%] md:w-32 min-[430px]:min-w-[initial] min-[430px]:max-w-[40%] md:max-w-[initial] xl:w-[8.5rem]",
				size: 136,
			}}
		/>
		<div
			class="absolute bottom-2 left-2 z-20 transition-opacity duration-300 ease-out {showSizeOverlay
				? 'opacity-100'
				: 'pointer-events-none opacity-0'}"
		>
			{#key showOverlayContent}
				{#if showOverlayContent}
					<div class="flex items-center">
						{#if product.sizeLabel && product.sizeLabel.type === ProductSizeLabelType.Serving}
							<div
								class="animate-zoomin relative z-[2] flex size-6 items-center justify-center rounded-full bg-primary"
							>
								<span class="size-label text-center text-2xs font-semibold leading-3 text-white">
									{selectedSize}
								</span>
							</div>
							{#if product.sizeLabel}
								{@const value = product.sizeLabel.values[productSizeKeyMap[selectedSize]]}
								<div class="portions-text-container relative z-[1] ml-1">
									<div
										class="animate-slideinleft-delayed text-nowrap rounded bg-white px-2 py-0.5 text-2xs font-semibold uppercase leading-5 text-secondary opacity-0"
									>
										{formatNumber(value, TENANT_LOCALE, false)}
										{inflectProductSizeLabelType[product.sizeLabel.type](value)}
									</div>
								</div>
							{/if}
						{/if}
					</div>
				{/if}
			{/key}
		</div>
	</a>
	<Form
		bind:form
		onsubmit={async () => {
			await submit(false);
		}}
		class="mt-4 grid min-h-[9.25rem] grid-rows-[auto,auto,auto] min-[430px]:min-h-[11.5rem] md:min-h-[9.25rem] lg:min-h-[12rem] xl:min-h-[9.25rem]"
	>
		<a
			class="line-clamp-2 h-14 text-md font-semibold leading-7 min-[430px]:text-base min-[430px]:leading-[1.3] md:text-md md:leading-[1.5]"
			href={linkToProduct}
		>
			{displayName}
		</a>
		<div
			class="mb-2 flex justify-between gap-0.5 min-[430px]:mb-0 min-[430px]:flex-col md:mb-2 md:flex-row lg:mb-0 lg:flex-col xl:mb-2 xl:flex-row"
		>
			<div class="flex flex-wrap items-center gap-x-3 md:gap-x-2 lg:gap-x-3">
				<!-- INFO: Minimum order quantity wins over multiple sizes. -->
				{#if product.minimumOrderQuantity > 1}
					<div
						class="flex flex-wrap items-center gap-2"
						class:opacity-0={isInCart}
						class:pointer-events-none={isInCart}
					>
						<span class="text-xs">Počet kusů</span>
						<NumberInput
							id="quantity"
							required
							min={product.minimumOrderQuantity}
							max={maximumOrderQuantity}
							class="h-9 w-32 p-1.5"
							value={product.minimumOrderQuantity}
						/>
					</div>
				{:else if shouldShowProductForCustomerSize(product)}
					<span class="pt-[0.3125rem] text-xs font-normal leading-5 min-[430px]:text-2xs md:text-xs">Velikost</span>
					<ProductSizeInputs
						{activeFilter}
						{product}
						bind:selectedSize
						isExpress={isWarehouseProduct}
						isSizeAddedToCart={isInCart}
						on:sizechange={handleSizeChange}
					/>
				{/if}
			</div>
			<span
				class="flex items-center text-md leading-7 min-[430px]:mb-1 min-[430px]:mt-3 md:my-0 lg:mb-2 lg:mt-3 xl:my-0 {customizable ||
				product.minimumOrderQuantity > 1
					? 'justify-between'
					: 'justify-end'}"
			>
				{#if customizable && canAddToCart}
					<Link
						class="hidden !py-[.175rem] text-2xs font-semibold min-[430px]:block md:hidden lg:block lg:text-xs xl:hidden"
						href={withConfigQueryParam(linkToProduct)}
						asButton
						variant="secondary">Upravit</Link
					>
				{/if}
				<span class="font-semibold">
					{#if enabledOption !== undefined && !isSimpleToggleProductOption(enabledOption)}
						Od
					{/if}
					{nbspify(formatPrice(priceWithEnabledOption.withTax, TENANT_LOCALE, false))}
				</span>
			</span>
		</div>
		<div class="flex items-end gap-4 {customizable && canAddToCart ? 'justify-between' : 'justify-end'}">
			{#if customizable && canAddToCart}
				<Link
					class="min-[430px]:hidden md:block lg:hidden xl:block"
					href={withConfigQueryParam(linkToProduct)}
					asButton
					variant="secondary"
				>
					Upravit
				</Link>
			{/if}
			<Button
				type="submit"
				variant={canAddToCart ? "primary" : "secondary"}
				class="whitespace-nowrap text-base font-semibold leading-5 min-[430px]:w-full min-[430px]:justify-center min-[430px]:text-xs md:w-fit md:justify-start md:text-base lg:w-full lg:justify-center xl:w-fit xl:justify-start {isInCart
					? 'pointer-events-none !border-tertiary !bg-tertiary !text-white hover:!border-tertiary hover:!bg-tertiary hover:!text-white focus:!border-tertiary focus:!bg-tertiary focus:!text-white active:!border-tertiary active:!bg-tertiary active:!text-white'
					: ''}"
				iconClass="w-[0.875rem]"
				icon={isInCart ? check : cake}
				disabled={!canAddToCart}
			>
				<div class="together">
					{#if !canAddToCart}
						{soldOutLabelNameMap[product.soldOutLabel ?? SoldOutLabel.Unavailable][TENANT_LOCALE]}
					{:else}
						<div class:opacity-0={isInCart}>Do krabice</div>
						<div class:opacity-0={!isInCart}>V krabici</div>
					{/if}
				</div>
			</Button>
		</div>
	</Form>
</div>

<style lang="postcss">
	.together {
		display: grid;
		grid-template-areas: "single";

		& > * {
			grid-area: single;
		}
	}

	@keyframes zoomin {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	@keyframes slideinleft {
		0% {
			transform: translateX(-100%);
			opacity: 0;
		}
		40% {
			opacity: 0;
		}
		60% {
			opacity: 1;
		}
		100% {
			transform: translateX(0);
			opacity: 1;
		}
	}

	.animate-zoomin {
		animation: zoomin 0.3s ease-out forwards;
	}

	.animate-slideinleft-delayed {
		animation: slideinleft 0.5s ease-out 0.2s forwards;
	}
</style>
